import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'components'

const imagePropType = PropTypes.shape({
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  filename: PropTypes.string,
  caption: PropTypes.string,
})

const TRANSITION_MS = 350
const DRAG_THRESHOLD = 50

/**
 * Internal carousel engine — supports smooth sliding, infinite looping,
 * mouse/touch drag, autoplay (paused on hover/drag), and fixed item widths.
 *
 * For infinite mode we prepend a clone of the last page and append a clone of
 * the first page, then silently snap back after each boundary animation.
 */
function Carousel({
  images,
  imagesPerPage,
  initialPage,
  onItemClick,
  gap,
  showDots,
  showArrows,
  autoPlay,
  autoPlayInterval,
}) {
  const totalPages = Math.ceil(images.length / imagesPerPage)
  const infinite = totalPages > 1

  // internalPage: 0 = clone of last, 1..totalPages = real, totalPages+1 = clone of first
  const [internalPage, setInternalPage] = useState(
    (initialPage || 0) + (infinite ? 1 : 0)
  )
  const [animated, setAnimated] = useState(true)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [pausedByUser, setPausedByUser] = useState(false)

  const dragStartX = useRef(null)
  const dragOffsetRef = useRef(0)
  const hoveredRef = useRef(false)
  const draggingRef = useRef(false)
  const wasDraggingRef = useRef(false)

  // Build slides with clones for infinite loop
  const pageGroups = []
  for (let i = 0; i < images.length; i += imagesPerPage) {
    pageGroups.push(images.slice(i, i + imagesPerPage))
  }
  const allSlides = infinite
    ? [pageGroups[totalPages - 1], ...pageGroups, pageGroups[0]]
    : pageGroups

  const itemWidth = `calc((100% - ${
    (imagesPerPage - 1) * gap
  }px) / ${imagesPerPage})`

  // Active dot index
  const dotPage = infinite
    ? (internalPage - 1 + totalPages) % totalPages
    : internalPage

  // --- Infinite loop: after animating to a clone, silently snap to the real slide ---
  useEffect(() => {
    if (!infinite || !animated) return
    if (internalPage === totalPages + 1) {
      const t = setTimeout(() => {
        setAnimated(false)
        setInternalPage(1)
      }, TRANSITION_MS)
      return () => clearTimeout(t)
    }
    if (internalPage === 0) {
      const t = setTimeout(() => {
        setAnimated(false)
        setInternalPage(totalPages)
      }, TRANSITION_MS)
      return () => clearTimeout(t)
    }
  }, [internalPage, infinite, totalPages, animated])

  // Re-enable animation one tick after a silent snap
  useEffect(() => {
    if (animated) return
    const t = setTimeout(() => setAnimated(true), 20)
    return () => clearTimeout(t)
  }, [animated])

  const autoplayActive = autoPlay && !pausedByUser

  // --- Autoplay ---
  useEffect(() => {
    if (!autoplayActive || totalPages <= 1) return
    const id = setInterval(() => {
      if (!hoveredRef.current && !draggingRef.current) {
        setAnimated(true)
        setInternalPage((p) => p + 1)
      }
    }, autoPlayInterval)
    return () => clearInterval(id)
  }, [autoplayActive, autoPlayInterval, totalPages])

  // --- Navigation ---
  const goNext = () => {
    setAnimated(true)
    setInternalPage((p) => (infinite ? p + 1 : Math.min(p + 1, totalPages - 1)))
  }
  const goPrev = () => {
    setAnimated(true)
    setInternalPage((p) => (infinite ? p - 1 : Math.max(p - 1, 0)))
  }
  const goTo = (realIdx) => {
    setAnimated(true)
    setInternalPage(infinite ? realIdx + 1 : realIdx)
  }

  const onViewportKeyDown = (e) => {
    if (e.target !== e.currentTarget) return
    if (totalPages <= 1) return
    const left = e.key === 'ArrowLeft' || e.keyCode === 37
    const right = e.key === 'ArrowRight' || e.keyCode === 39
    if (!left && !right) return
    e.preventDefault()
    if (left) goPrev()
    else goNext()
  }

  // --- Drag ---
  const startDrag = (clientX) => {
    dragStartX.current = clientX
    dragOffsetRef.current = 0
    draggingRef.current = true
    setIsDragging(true)
  }
  const moveDrag = (clientX) => {
    if (!draggingRef.current) return
    const delta = clientX - dragStartX.current
    dragOffsetRef.current = delta
    setDragOffset(delta)
  }
  const endDrag = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setIsDragging(false)
    const delta = dragOffsetRef.current
    dragOffsetRef.current = 0
    setDragOffset(0)
    if (delta < -DRAG_THRESHOLD) {
      wasDraggingRef.current = true
      goNext()
    } else if (delta > DRAG_THRESHOLD) {
      wasDraggingRef.current = true
      goPrev()
    }
  }

  const trackStyle = {
    transform: `translateX(calc(-100% * ${internalPage} + ${dragOffset}px))`,
    transition:
      isDragging || !animated
        ? 'none'
        : `transform ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    willChange: 'transform',
    cursor: isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
  }

  return (
    <div
      className="image-carousel"
      onMouseEnter={() => {
        hoveredRef.current = true
      }}
      onMouseLeave={() => {
        hoveredRef.current = false
        endDrag()
      }}
    >
      <div
        className="image-carousel-viewport"
        tabIndex={0}
        role="group"
        aria-label="Image carousel, use arrow keys to change page"
        onKeyDown={onViewportKeyDown}
        onMouseDown={(e) => {
          e.preventDefault()
          startDrag(e.clientX)
        }}
        onMouseMove={(e) => moveDrag(e.clientX)}
        onMouseUp={endDrag}
        onTouchStart={(e) => startDrag(e.touches[0].clientX)}
        onTouchMove={(e) => {
          e.preventDefault()
          moveDrag(e.touches[0].clientX)
        }}
        onTouchEnd={endDrag}
      >
        <div className="image-carousel-track" style={trackStyle}>
          {allSlides.map((slideImages, slideIdx) => {
            // Map cloned slide index back to a real page index for globalIndex
            const realPageIdx = !infinite
              ? slideIdx
              : slideIdx === 0
              ? totalPages - 1
              : slideIdx === totalPages + 1
              ? 0
              : slideIdx - 1

            return (
              <div
                key={slideIdx}
                className="image-carousel-page"
                style={{ gap: `${gap}px` }}
              >
                {slideImages.map((image, i) => {
                  const globalIndex = realPageIdx * imagesPerPage + i
                  return (
                    <button
                      key={`${image.src}-s${slideIdx}-${i}`}
                      type="button"
                      className="image-carousel-item"
                      style={{
                        flexBasis: itemWidth,
                        flexShrink: 0,
                        flexGrow: 0,
                      }}
                      onClick={() => {
                        if (wasDraggingRef.current) {
                          wasDraggingRef.current = false
                          return
                        }
                        if (onItemClick) onItemClick(globalIndex)
                      }}
                      aria-label={`View image${
                        image.alt || image.caption
                          ? `: ${image.alt || image.caption}`
                          : ' ' + (globalIndex + 1)
                      }`}
                    >
                      <div className="image-carousel-img-wrapper">
                        <img
                          src={image.src}
                          alt={image.alt || image.caption || ''}
                          draggable={false}
                        />
                      </div>
                      {image.caption && (
                        <p className="image-carousel-caption">
                          {image.caption}
                        </p>
                      )}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>

      {showDots && totalPages > 1 && (
        <div
          className="image-carousel-dots"
          role="group"
          aria-label="Image pages"
        >
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`image-carousel-dot${i === dotPage ? ' active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to page ${i + 1} of ${totalPages}`}
              aria-current={i === dotPage ? 'true' : undefined}
            />
          ))}
        </div>
      )}

      {autoPlay && totalPages > 1 && (
        <div className="image-carousel-autoplay-control">
          <button
            type="button"
            className="image-carousel-autoplay-toggle"
            aria-pressed={autoplayActive ? 'true' : 'false'}
            onClick={() => setPausedByUser((p) => !p)}
          >
            {pausedByUser ? 'Play slideshow' : 'Pause slideshow'}
          </button>
        </div>
      )}

      {showArrows && totalPages > 1 && (
        <div className="image-carousel-arrows">
          <button
            type="button"
            className="image-carousel-nav-btn"
            onClick={goPrev}
            aria-label="Previous images"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="43"
              height="26"
              viewBox="0 0 43 26"
              fill="none"
            >
              <path
                d="M1.5 12.5417L12.5417 1.5M1.5 12.5417L12.5417 23.5833M1.5 12.5417H41.25"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="image-carousel-nav-btn"
            onClick={goNext}
            aria-label="Next images"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="43"
              height="26"
              viewBox="0 0 43 26"
              fill="none"
            >
              <path
                d="M41.25 12.5417L30.2083 1.5M41.25 12.5417L30.2083 23.5833M41.25 12.5417H1.5"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

Carousel.propTypes = {
  images: PropTypes.arrayOf(imagePropType).isRequired,
  imagesPerPage: PropTypes.number.isRequired,
  initialPage: PropTypes.number,
  onItemClick: PropTypes.func,
  gap: PropTypes.number.isRequired,
  showDots: PropTypes.bool,
  showArrows: PropTypes.bool,
  autoPlay: PropTypes.bool,
  autoPlayInterval: PropTypes.number,
}

Carousel.defaultProps = {
  initialPage: 0,
  onItemClick: undefined,
  showDots: false,
  showArrows: false,
  autoPlay: false,
  autoPlayInterval: 4000,
}

/**
 * Reusable image carousel.
 *
 * Thumbnail grid (with dots) supports infinite looping, autoplay, and
 * mouse/touch drag. When `autoPlay` is true and there is more than one page,
 * a **Pause slideshow** / **Play slideshow** control is shown (WCAG 2.2.2).
 * Autoplay is disabled when `prefers-reduced-motion: reduce`.
 * Clicking an image opens an expanded modal carousel (with arrows) at the
 * same image; the modal carousel does not autoplay.
 *
 * @example
 * <ImageCarousel
 *   images={artist.spotlightImages}
 *   imagesPerPage={4}
 *   modalImagesPerPage={2}
 *   autoPlay
 *   autoPlayInterval={5000}
 * />
 */
function ImageCarousel({
  images,
  imagesPerPage,
  modalImagesPerPage,
  autoPlay,
  autoPlayInterval,
  modalOnly,
  onClose,
}) {
  // When modalOnly, start with the modal open at index 0
  const [modalIndex, setModalIndex] = useState(modalOnly ? 0 : null)
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setReduceMotion(!!mq.matches)
    apply()
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply)
      return () => mq.removeEventListener('change', apply)
    }
    mq.addListener(apply)
    return () => mq.removeListener(apply)
  }, [])

  const autoplayEnabled = autoPlay && !reduceMotion

  if (!images || images.length === 0) return null

  const handleModalClose = () => {
    setModalIndex(null)
    if (onClose) onClose()
  }

  return (
    <>
      {!modalOnly && (
        <Carousel
          images={images}
          imagesPerPage={imagesPerPage}
          onItemClick={setModalIndex}
          gap={16}
          showDots
          autoPlay={autoplayEnabled}
          autoPlayInterval={autoPlayInterval}
        />
      )}
      {modalIndex !== null && (
        <Modal onClose={handleModalClose} className="image-carousel-modal">
          <Carousel
            images={images}
            imagesPerPage={modalImagesPerPage}
            initialPage={Math.floor(modalIndex / modalImagesPerPage)}
            gap={24}
            showArrows
          />
        </Modal>
      )}
    </>
  )
}

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(imagePropType).isRequired,
  imagesPerPage: PropTypes.number,
  modalImagesPerPage: PropTypes.number,
  autoPlay: PropTypes.bool,
  autoPlayInterval: PropTypes.number,
  /** Skip the thumbnail grid and open the modal directly. Use with `onClose`. */
  modalOnly: PropTypes.bool,
  onClose: PropTypes.func,
}

ImageCarousel.defaultProps = {
  imagesPerPage: 4,
  modalImagesPerPage: 2,
  autoPlay: true,
  autoPlayInterval: 4000,
  modalOnly: false,
  onClose: undefined,
}

export default ImageCarousel
